import config from '../../config/config';
import {
  StationParameters,
  StationStatusChangeParameters,
} from '../../interface/charging-station.interface';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Fetch the charging station list with pagination
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the charging stations
   */
  fetchChargingStations: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station`, {
      ...parameters,
    });
  },
  /**
   * Fetch the charging station list with pagination
   * @returns {any} A promise that resolves with the list filters
   */
  fetchChargingStationsListFilters: (): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/list-filters`);
  },
  /**
   * Fetch charging station by Id
   * @param {any} id - The charging station ID
   * @returns {any} A promise that resolves with the charging station
   */
  fetchChargingStationById: (id: string): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/${id}`);
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the exported data
   */
  exportChargingStations: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/export-data`, {
      ...parameters,
    });
  },
  /**
   * Create the station api call function
   * @param {any} data - The station parameters
   * @returns {any} A promise that resolves with the creation response
   */
  createChargingStation: (
    data: StationParameters = {
      name: '',
      email: '',
      role: '',
      password: '',
    },
  ): Promise<any> => {
    const parameters = { ...data };
    return postRequest(`${config.commonApiUrl}api/super-admin/v1/users`, {
      ...parameters,
    });
  },
  /**
   * Update the station api call function
   * @param {any} id - The charging station ID
   * @param {any} data - The station parameters
   * @returns {any} A promise that resolves with the update response
   */
  editChargingStation: (
    id: string,
    data: StationParameters = {
      name: '',
      email: '',
      role: '',
    },
  ): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(`${config.commonApiUrl}api/super-admin/v1/users/${id}`, {
      ...parameters,
    });
  },
  /**
   * Delete the station api call function
   * @param {any} id - The charging station ID
   * @returns {any} A promise that resolves with the deletion response
   */
  deleteChargingStation: (id: String): Promise<any> => {
    return deleteRequest(`${config.commonApiUrl}api/super-admin/v1/users/${id}`);
  },
  /**
   * Delete the station api call function
   * @param {any} id - The charging station ID
   * @param {any} data - The status change parameters
   * @returns {any} A promise that resolves with the status change response
   */
  statusChangeChargingStation: (id: string, data: StationStatusChangeParameters): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/${id}`, {
      ...parameters,
    });
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} id - The charging station ID
   * @returns {any} A promise that resolves with the charging points
   */
  fetchChargingPointByStationId: (id: string): Promise<any> => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-point/charging-station/${id}`,
    );
  },

  /**
   * pending notification count
   * @returns {any} A promise that resolves with the pending notification count
   */
  fetchPendingNotificationCount: (): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/pending-count`);
  },
  /**
   * Delete the device status
   * @param {any} id - The device/station ID
   * @returns {any} A promise that resolves with the deletion response
   */
  deleteDeviceStatus: (id: string): Promise<any> => {
    return deleteRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station/cancel-schedule-delete-station/${id}`,
    );
  },
  /**
   * Get method The station you are deleting has active charging point
   * @param {any} id - The station ID to check
   * @returns {any} A promise that resolves with the deletion check response
   */
  getStationDeletionCheck: (id: string): Promise<any> => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station/deletion-check/${id}`,
    );
  },
  /**
   * Manually syncs charging station data with the OCPI gateway.
   * @returns {any} A promise that resolves with the sync response.
   */
  syncOcpiChargingStations: (): Promise<any> => {
    return postRequest(`${config.commonApiUrl}api/ocpi/v1/charging-station/sync`, {});
  },
};
